import { useQuery, useQueryClient } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { ComputeFactory } from '../../models/ComputeFactory'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { isPastDate } from '../../utils'
import { dateFormat } from '../../utils/dates'
import { cancellationFeeString } from '../../utils/records'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps } from '../PocketComponent'
import { usePSComponent } from '../PocketComponent'
import { CancellationModal } from '../common/CancellationModal'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { SourceModalWorkflow } from '../contacts/SourceModalWorkflow'
import { SubscriptionView } from './SubscriptionView'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    if (!urlParams.id) {
        throw new RecordNotFoundError('Subscription not found')
    }

    await RecordFactory.getInstance().fetchSubscription({ id: urlParams.id } as any, 'view')
    const subscription = await RecordFactory.getInstance().loadSubscription(urlParams.id)
    if (!subscription) {
        throw new RecordNotFoundError('Subscription not found')
    }

    const client = subscription.client
    const item = subscription.plan
    if (!client || !item) {
        throw new RecordNotFoundError('Subscription not found')
    }

    const [payments, packages] = await Promise.all([
        ComputeFactory.getInstance().fetchSubscriptionPayments(subscription),
        ComputeFactory.getInstance().fetchSubscriptionPackages(subscription),
    ])

    return { subscription, client, item, payments, packages }
}

const render = ({
    data,
    base: { logAndDisplayError, me },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { client, item, payments, packages } = data

    const [subscription, setSubscription] = useState(data.subscription)

    const navigate = useNavigate()

    usePageTitle('Subscription')

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
    } | null>(null)

    const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false)
    const [isSourcesModalOpen, setIsSourcesModalOpen] = useState(false)

    const queryClient = useQueryClient()
    const { data: clientSources } = useQuery({
        queryKey: ['sources', client.id],
        queryFn: () => Source.validClientSources(client.id ?? ''),
    })

    const handleDeleteSource = async (source: Source) => {
        if (source.id === undefined) {
            return
        }
        await RecordFactory.getInstance().deleteSource(source.id)
        await queryClient.invalidateQueries({ queryKey: ['sources', client.id] })
    }

    const handleSelectSource = async (source: Source) => {
        setIsSourcesModalOpen(false)
        const result = await RecordFactory.getInstance().changeSubscriptionSource(
            subscription,
            source
        )
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        setSubscription(result.value)
    }

    const handleSkip = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().skipNextRenewal(subscription)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setConfirmationModalData({
            title: 'Success',
            confirmationButtonText: 'OK',
            body: `The next subscription payment was skipped. The next payment is due on ${dateFormat(result.value.nextInvoiceDate)}.`,
            onConfirm: () => window.location.reload(),
        })
    }

    const handleCancel = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().cancelSubscription(subscription, false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setConfirmationModalData({
            title: 'Success',
            confirmationButtonText: 'OK',
            body: 'This subscription has been cancelled.',
            onConfirm: () => setConfirmationModalData(null),
        })
    }

    const handleCancellationComplete = () => {
        if (subscription.channel && subscription.client?.id) {
            navigate(`/messages/${subscription.client.id}`)
        }
        else {
            navigate('/dashboards/subscriptions')
        }
    }

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        if (subscription.owner?.id === me.id) {
            buttons.push({
                title: 'Edit',
                onClick: () => navigate(`/subscriptions/${subscription.id}/edit`),
            })
        }

        if (
            subscription.owner?.id === me.id &&
            subscription.confirmedDate &&
            !subscription.cancelledDate &&
            !subscription.completedDate &&
            subscription.instances !== 1
        ) {
            buttons.push({
                title: 'Skip payment',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Skip next payment?',
                        confirmationButtonText: 'Yes, skip',
                        body: 'This action cannot be undone',
                        onConfirm: handleSkip,
                    }),
            })
        }
        else if (!subscription.cancelledDate && !subscription.confirmedDate) {
            buttons.push({
                title: 'Charge',
                onClick: () => navigate(`/charge/subscription/${subscription.id}`),
            })
        }
        else {
            buttons.push({
                title: 'Copy',
                onClick: () => navigate(`/subscriptions/${subscription.id}/copy`),
            })
        }

        if (!subscription.cancelledDate && !subscription.completedDate) {
            buttons.push({
                title: 'Cancel',
                onClick: () => {
                    if (subscription.confirmedDate && !subscription.cancelledDate) {
                        setIsCancellationModalOpen(true)
                        return
                    }

                    const date = subscription.instances
                        ? subscription.date
                        : subscription.nextInvoiceDate
                    const isLate =
                        !!subscription.vendor?.cancellationRate &&
                        (!date ||
                            isPastDate(date, subscription.vendor?.cancellationDuration ?? 0) ||
                            (subscription.instances === 0 && subscription.confirmedDate))

                    setConfirmationModalData({
                        title: 'Cancel Subscription',
                        confirmationButtonText:
                            subscription.vendor !== undefined &&
                            isLate &&
                            subscription.source &&
                            subscription.confirmedDate
                                ? `Cancel and pay ${cancellationFeeString(subscription.vendor)} fee`
                                : 'Cancel subscription',
                        body: 'Would you like to cancel this subscription?',
                        onConfirm: handleCancel,
                    })
                },
            })
        }

        if (
            !subscription.completedDate &&
            !subscription.cancelledDate &&
            subscription.confirmedDate
        ) {
            buttons.push({
                title: 'Select card',
                onClick: () => setIsSourcesModalOpen(true),
            })
        }

        return uniqBy(buttons, (button) => button.title)
    })()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^(\/subscriptions.*\/(new|edit|copy))|(\/change-card.*)|(.*\/add-card)/,
    })

    return (
        <BaseRecordView
            title="Subscription"
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Subscriptions',
                          onClick: () => navigate('/dashboards/subscriptions'),
                      }
            }
            headerButtons={headerButtons}
            tag={subscription.statusTag ?? undefined}
        >
            <SubscriptionView
                subscription={subscription}
                isWorkflowReviewStep={false}
                client={client}
                item={item}
                payments={payments}
                packages={packages}
            />

            {confirmationModalData !== null && (
                <ConfirmationModal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    onConfirm={confirmationModalData.onConfirm}
                    title={confirmationModalData.title}
                    confirmButtonText={confirmationModalData.confirmationButtonText}
                    shouldHideCancelButton
                >
                    {confirmationModalData.body}
                </ConfirmationModal>
            )}

            {subscription.owner && (
                <CancellationModal
                    isOpen={isCancellationModalOpen}
                    onClose={() => setIsCancellationModalOpen(false)}
                    target={{ type: 'subscription', subscription }}
                    vendor={subscription.owner}
                    me={me}
                    onCancellationComplete={handleCancellationComplete}
                />
            )}

            <SourceModalWorkflow
                isOpen={isSourcesModalOpen}
                onClose={() => setIsSourcesModalOpen(false)}
                sources={clientSources ?? []}
                onCreateSource={{
                    action: 'create-source',
                    client,
                    callback: () =>
                        queryClient.invalidateQueries({ queryKey: ['sources', client.id] }),
                }}
                onDeleteSource={handleDeleteSource}
                onSelectSource={handleSelectSource}
                selectedSourceID={subscription.source?.id ?? null}
            />
        </BaseRecordView>
    )
}

export const SubscriptionPage = usePSComponent(render, viewWillAppear)
